import { useEffect, useRef, useState } from 'react';
import type { MaterialItem } from '@/api/materialInfo';
import { getCurrentUser, type CurrentUser } from '@/api/agent';
import { getWatermarkConfig, type WatermarkConfig } from '@/api/config';
import PdfViewer from './PdfViewer';
import { WordViewer, ExcelViewer } from 'react-file-preview-auto';
import PptViewer from './PptViewer';
import WatermarkOverlay from './WatermarkOverlay';
import type { ExcelData } from 'react-file-preview-auto';
import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist';
import 'react-file-preview-auto/dist/index.css';

// react-file-preview-auto 会将 workerSrc 覆盖为 CDN 地址（已失效），此处重新设回本地文件
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface PreviewModalProps {
  material: MaterialItem | null;
  onClose: () => void;
}

function canDownload(material: MaterialItem): boolean {
  if (material.isVideo) return false;
  return material.downloadAllowed;
}

function hasWatermark(material: MaterialItem): boolean {
  return material.type === 'PDF' || material.type === 'PPT' || material.type === 'Word' || material.type === 'Excel';
}

export default function PreviewModal({ material, onClose }: PreviewModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [excelData, setExcelData] = useState<ExcelData | null>(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [wmConfig, setWmConfig] = useState<WatermarkConfig | null>(null);

  useEffect(() => {
    getCurrentUser().then(setUser).catch(() => {});
    getWatermarkConfig().then(setWmConfig).catch(() => {});
  }, []);

  // 加载 Excel 文件数据用于客户端渲染
  useEffect(() => {
    if (material?.type === 'Excel' && material.fileUrl) {
      setExcelLoading(true);
      setExcelData(null);
      fetch(material.fileUrl)
        .then((res) => res.arrayBuffer())
        .then((buffer) => {
          const workbook = XLSX.read(buffer, { type: 'array' });
          const sheetsData: ExcelData['sheetsData'] = {};
          workbook.SheetNames.forEach((sheetName) => {
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
            const maxLength = Math.max(...jsonData.map((row) => row?.length || 0), 0);
            sheetsData[sheetName] = {
              data: jsonData.map((row) => {
                const paddedRow = Array.isArray(row) ? [...row] : [];
                while (paddedRow.length < maxLength) paddedRow.push(null);
                return paddedRow;
              }),
              maxColumns: maxLength,
            };
          });
          setExcelData({
            sheets: workbook.SheetNames,
            currentSheet: workbook.SheetNames[0],
            sheetsData,
          });
        })
        .catch(() => {})
        .finally(() => setExcelLoading(false));
    } else {
      setExcelData(null);
    }
  }, [material?.fileUrl, material?.type]);

  useEffect(() => {
    if (!material) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [material, onClose]);

  if (!material) return null;

  const downloadAllowed = canDownload(material);
  const showWatermark = hasWatermark(material);

  // 根据系统水印配置构建水印文本
  const now = new Date();
  const timestamp = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
  const wmParts: string[] = [];
  if (wmConfig?.WM_USEACCOUNT_SHOW && user?.email) wmParts.push(user.email);
  if (wmConfig?.WM_COMPANY_SHOW && user?.company) wmParts.push(user.company);
  if (wmConfig?.WM_ACCESSDATE_SHOW) wmParts.push(timestamp);
  const watermarkText = wmParts.join('  ·  ');

  const typeIconMap: Record<string, string> = {
    PDF: 'ri-file-pdf-line',
    PPT: 'ri-file-ppt-line',
    Excel: 'ri-file-excel-line',
    Word: 'ri-file-word-line',
  };
  const typeIcon = typeIconMap[material.type] ?? 'ri-file-line';

  const typeColorMap: Record<string, string> = {
    PDF: 'text-red-500',
    PPT: 'text-orange-500',
    Excel: 'text-green-600',
    Word: 'text-blue-600',
  };
  const typeColor = typeColorMap[material.type] ?? 'text-gray-500';

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl overflow-hidden flex flex-col w-full max-w-5xl"
        style={{
          height: '90vh',
          maxHeight: '900px',
          boxShadow: '0 24px 48px rgba(0,0,0,0.25)',
          animation: 'modalIn 0.2s ease-out',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-8 h-8 flex items-center justify-center shrink-0 ${typeColor}`}>
              <i className={`${typeIcon} text-xl`}></i>
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 truncate">{material.title}</h2>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
                <p className="text-xs text-gray-400">{material.fileSize} · {material.type} Document</p>
                {showWatermark && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-600 border border-amber-200 whitespace-nowrap">
                    <i className="ri-shield-check-line text-xs"></i>Watermark Protected
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-4">
            {downloadAllowed ? (
              <button
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white whitespace-nowrap cursor-pointer transition-all hover:opacity-90"
                style={{ background: '#0069BD' }}
                onClick={() => {
                  const token = localStorage.getItem('agent_token');
                  const params = new URLSearchParams({ id: material.id });
                  if (token) params.set('token', token);
                  window.open(`/api/agentfront/download.htm?${params.toString()}`, '_blank');
                }}
              >
                <i className="ri-download-line text-sm"></i>
                <span className="hidden sm:inline">Download</span>
              </button>
            ) : (
              <div className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-400 bg-gray-100 whitespace-nowrap cursor-not-allowed">
                <i className="ri-lock-line text-sm"></i>
                <span className="hidden sm:inline">View Only</span>
              </div>
            )}
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Viewer Area */}
        <div className="flex-1 relative overflow-hidden bg-gray-50">
          {material.type === 'PDF' && material.fileUrl ? (
            <div className="relative w-full h-full">
              <PdfViewer url={material.fileUrl} />
              {showWatermark && watermarkText && <WatermarkOverlay text={watermarkText} />}
            </div>
          ) : material.type === 'Word' && material.fileUrl ? (
            <div className="relative w-full h-full">
              <WordViewer url={material.fileUrl} />
              {showWatermark && watermarkText && <WatermarkOverlay text={watermarkText} />}
            </div>
          ) : material.type === 'Excel' && material.fileUrl ? (
            <div className="relative w-full h-full">
              {excelLoading && (
                <div className="flex items-center justify-center h-full bg-white">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-3 border-[#0069BD] border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-gray-500">加载表格数据...</span>
                  </div>
                </div>
              )}
              {!excelLoading && excelData && <ExcelViewer excelData={excelData} />}
              {!excelLoading && !excelData && (
                <div className="flex flex-col items-center justify-center h-full gap-3 bg-white">
                  <i className="ri-error-warning-line text-4xl text-amber-500" />
                  <span className="text-sm text-gray-600">无法加载 Excel 文件</span>
                </div>
              )}
              {showWatermark && watermarkText && <WatermarkOverlay text={watermarkText} />}
            </div>
          ) : material.type === 'PPT' && material.fileUrl ? (
            <div className="relative w-full h-full">
              <PptViewer url={material.fileUrl} />
              {showWatermark && watermarkText && <WatermarkOverlay text={watermarkText} />}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <i className="ri-file-line text-6xl text-gray-300"></i>
              <div className="text-center px-8">
                <p className="text-sm font-semibold text-gray-700">{material.title}</p>
                <p className="text-xs text-gray-400 mt-1">{material.type} · {material.fileSize}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
